/**
 * Chaves (conta/rubrica + competência) que o backend usa para montar o projeto.
 * @param {Array<object>} linhas
 * @param {string} [campoChave] campo da linha que identifica o item (PIS/COFINS: conta; E-Social: rubrica)
 */
export function chavesDasLinhas(linhas, campoChave = "ecdContaContabil") {
  return (Array.isArray(linhas) ? linhas : []).map((linha) => ({
    chaveConta: linha[campoChave],
    competencia: linha.competencia,
  }));
}

const somaPisCofins = (linha) => (Number(linha.pis) || 0) + (Number(linha.cofins) || 0);

/**
 * O que entraria no projeto: só linhas com base legal. O valor é só uma prévia — quem manda é o
 * backend, que recalcula a partir da sumarização.
 * @param {Array<{ baseLegalId?: number|null }>} linhas
 * @param {(linha: object) => number} [valorDaLinha] padrão: PIS + COFINS
 * @returns {{ itens: number, valorTotal: number }}
 */
export function resumoDaSelecao(linhas, valorDaLinha = somaPisCofins) {
  const validas = (Array.isArray(linhas) ? linhas : []).filter((linha) => linha.baseLegalId != null);
  const soma = validas.reduce((acc, linha) => acc + valorDaLinha(linha), 0);
  return { itens: validas.length, valorTotal: Math.round((soma + Number.EPSILON) * 100) / 100 };
}

/** E-Social: item = rubrica × competência; valor = total com SELIC. */
export function selecaoEsocial(linhas) {
  return {
    chaves: chavesDasLinhas(linhas, "codigoRubrica"),
    resumo: resumoDaSelecao(linhas, (linha) => Number(linha.valorTotalSelic) || 0),
  };
}

const arredondar2 = (valor) => Math.round((valor + Number.EPSILON) * 100) / 100;

/**
 * ICMS-ST: item = NCM × EAN × modelo × origem × competência (`chaveItem`). Entram as linhas com base
 * legal e com o detalhe de recuperável/complemento gravado pelo motor (apuração reprocessada). O valor
 * é a prévia do crédito: por mês, recuperável − complemento, sem ficar negativo.
 */
export function selecaoIcmsSt(linhas) {
  const lista = Array.isArray(linhas) ? linhas : [];
  const validas = lista.filter(
    (linha) => linha.baseLegalId != null && linha.creditoRecuperavel != null && linha.creditoComplemento != null
  );
  const porMes = new Map();
  validas.forEach((linha) => {
    const mes = String(linha.competencia).slice(0, 7);
    porMes.set(mes, (porMes.get(mes) || 0) + linha.creditoRecuperavel - linha.creditoComplemento);
  });
  const valorTotal = [...porMes.values()].reduce((soma, valor) => soma + Math.max(arredondar2(valor), 0), 0);
  return {
    chaves: chavesDasLinhas(lista, "chaveItem"),
    resumo: { itens: validas.length, valorTotal: arredondar2(valorTotal) },
  };
}
